import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AccountService } from '../core/account.service';
import { AuthService } from '../core/auth.service';
import { formatWhen } from '../core/format';
import { errorMessage } from '../core/http-error';
import { LEAD_STATUSES, leadStatusLabel } from '../core/lead-status';
import { Agent, CrmNotice, Lead } from '../core/models';
import { NoticeBoard } from '../core/notice-board';

@Component({
  selector: 'app-crm',
  templateUrl: './crm.html',
})
export class CrmPage implements OnDestroy {
  private readonly http = inject(HttpClient);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly account = inject(AccountService);
  protected readonly auth = inject(AuthService);
  protected readonly board = inject(NoticeBoard);
  protected readonly when = formatWhen;
  protected readonly noticesOpen = signal(false);
  protected readonly statuses = LEAD_STATUSES;
  private timer = 0;
  protected readonly leads = signal<Lead[]>([]);
  protected readonly agents = signal<Agent[]>([]);
  protected readonly failed = signal<string | null>(null);
  protected readonly notice = signal<string | null>(null);
  protected readonly composer = signal<'lead' | 'agent' | null>(null);
  protected readonly saving = signal(false);

  protected readonly firstName = signal('');
  protected readonly lastName = signal('');
  protected readonly email = signal('');
  protected readonly phone = signal('');
  protected readonly country = signal('');
  protected readonly status = signal('NEW');
  protected readonly assigneeId = signal('');
  protected readonly query = signal('');
  protected readonly countryFilter = signal('');
  protected readonly statusFilter = signal('');
  protected readonly assigneeFilter = signal('');
  protected readonly checked = signal<number[]>([]);
  protected readonly massStatus = signal('');
  protected readonly massAgents = signal<number[]>([]);
  protected readonly massUnassign = signal(false);

  protected readonly agentName = signal('');
  protected readonly agentEmail = signal('');
  protected readonly agentPhone = signal('');
  protected readonly agentPassword = signal('');
  protected readonly pendingDelete = signal<Lead | null>(null);
  protected readonly deleting = signal(false);

  protected readonly admin = computed(() => this.auth.user()?.role === 'ADMIN');
  protected readonly countries = computed(() =>
    [...new Set(this.leads().map((lead) => lead.country).filter((country) => !!country))].sort((a, b) => a.localeCompare(b)),
  );
  protected readonly assigneeOptions = computed(() => {
    const byId = new Map<number, string>();
    for (const agent of this.agents()) {
      byId.set(agent.id, agent.name);
    }
    for (const lead of this.leads()) {
      if (lead.assigneeId != null && lead.assigneeName) {
        byId.set(lead.assigneeId, lead.assigneeName);
      }
    }
    return [...byId.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  });
  protected readonly allAgentsOn = computed(() => {
    const agents = this.agents();
    const picked = this.massAgents();
    return agents.length > 0 && agents.every((agent) => picked.includes(agent.id));
  });
  protected readonly shown = computed(() => {
    const term = this.query().trim().toLowerCase();
    const country = this.countryFilter();
    const status = this.statusFilter();
    const assignee = this.assigneeFilter();
    return this.leads().filter((lead) => {
      if (country && lead.country !== country) {
        return false;
      }
      if (status && lead.status !== status) {
        return false;
      }
      if (assignee === 'none' && lead.assigneeId != null) {
        return false;
      }
      if (assignee && assignee !== 'none' && String(lead.assigneeId ?? '') !== assignee) {
        return false;
      }
      if (!term) {
        return true;
      }
      return [lead.firstName, lead.lastName, lead.country, lead.email, lead.phone, lead.status, lead.assigneeName, lead.previousAssigneeName]
        .join(' ')
        .toLowerCase()
        .includes(term);
    });
  });

  constructor() {
    const params = this.route.snapshot.queryParamMap;
    this.query.set(params.get('q') ?? '');
    this.countryFilter.set(params.get('country') ?? '');
    this.statusFilter.set(params.get('status') ?? '');
    this.assigneeFilter.set(params.get('agent') ?? '');
    this.load();
    this.timer = window.setInterval(() => this.load(), 15000);
  }

  ngOnDestroy(): void {
    window.clearInterval(this.timer);
  }

  protected load(): void {
    this.http.get<Lead[]>('/api/crm/leads').subscribe({
      next: (rows) => {
        this.leads.set(rows);
        this.failed.set(null);
      },
      error: (error: unknown) => this.failed.set(errorMessage(error, 'Could not load the leads.')),
    });
    this.board.refresh();
    if (this.auth.user()?.role === 'ADMIN') {
      this.http.get<Agent[]>('/api/crm/agents').subscribe({
        next: (rows) => this.agents.set(rows),
        error: () => this.agents.set([]),
      });
    }
  }

  protected startLead(): void {
    this.composer.set('lead');
    this.firstName.set('');
    this.lastName.set('');
    this.email.set('');
    this.phone.set('');
    this.country.set('');
    this.status.set('NEW');
    this.assigneeId.set('');
  }

  protected openNotice(item: CrmNotice): void {
    this.board.acknowledge(item, (saved) => {
      if (!saved.openable || !saved.leadId) {
        return;
      }
      this.noticesOpen.set(false);
      void this.router.navigate(['/crm/leads', saved.leadId], {
        queryParams: {
          country: this.countryFilter() || null,
          status: this.statusFilter() || null,
          agent: this.assigneeFilter() || null,
          q: this.query().trim() || null,
        },
      });
    });
  }

  protected open(lead: Lead): void {
    void this.router.navigate(['/crm/leads', lead.id], {
      queryParams: {
        country: this.countryFilter() || null,
        status: this.statusFilter() || null,
        agent: this.assigneeFilter() || null,
        q: this.query().trim() || null,
      },
    });
  }

  protected saveLead(event?: Event): void {
    event?.preventDefault();
    const body = {
      firstName: this.firstName().trim(),
      lastName: this.lastName().trim(),
      email: this.email().trim(),
      phone: this.phone().trim(),
      country: this.country().trim(),
      status: this.status(),
      assigneeId: this.assigneeId() ? Number(this.assigneeId()) : null,
    };
    this.saving.set(true);
    this.http.post<Lead>('/api/crm/leads', body).subscribe({
      next: (lead) => {
        this.saving.set(false);
        this.composer.set(null);
        this.open(lead);
      },
      error: (error: unknown) => {
        this.saving.set(false);
        this.failed.set(errorMessage(error, 'Could not save the lead.'));
      },
    });
  }

  protected keep(event: Event): void {
    event.stopPropagation();
  }

  protected toggle(id: number, on: boolean): void {
    this.checked.update((ids) => (on ? (ids.includes(id) ? ids : [...ids, id]) : ids.filter((item) => item !== id)));
  }

  protected allShownChecked(): boolean {
    const rows = this.shown();
    return rows.length > 0 && rows.every((lead) => this.checked().includes(lead.id));
  }

  protected toggleAll(on: boolean): void {
    this.checked.set(on ? this.shown().map((lead) => lead.id) : []);
  }

  protected toggleAllAgents(on: boolean): void {
    this.massUnassign.set(false);
    this.massAgents.set(on ? this.agents().map((agent) => agent.id) : []);
  }

  protected toggleMassAgent(id: number, on: boolean): void {
    this.massUnassign.set(false);
    this.massAgents.update((ids) => (on ? [...ids, id] : ids.filter((item) => item !== id)));
  }

  protected setUnassign(on: boolean): void {
    this.massUnassign.set(on);
    if (on) {
      this.massAgents.set([]);
    }
  }

  protected applyMass(event?: Event): void {
    event?.preventDefault();
    const selected = new Set(this.checked());
    const visible = this.shown().map((lead) => lead.id).filter((id) => selected.has(id));
    const hidden = this.checked().filter((id) => !visible.includes(id));
    const ids = [...new Set([...visible, ...hidden])];
    const agents = this.massAgents();
    const unassign = this.massUnassign();
    const changeAssignee = unassign || agents.length > 0;
    if (ids.length === 0 || (!changeAssignee && !this.massStatus())) {
      return;
    }
    this.saving.set(true);
    this.http
      .post<number>('/api/crm/leads/mass', {
        ids,
        status: this.massStatus() || null,
        assigneeId: null,
        changeAssignee,
        assigneeIds: unassign ? [] : agents,
      })
      .subscribe({
        next: () => {
          this.saving.set(false);
          this.checked.set([]);
          this.massStatus.set('');
          this.massAgents.set([]);
          this.massUnassign.set(false);
          if (agents.length > 1) {
            this.notice.set(`${ids.length} leads split across ${agents.length} agents.`);
          } else if (agents.length === 1) {
            this.notice.set(`${ids.length} leads assigned.`);
          } else if (unassign) {
            this.notice.set(`${ids.length} leads unassigned.`);
          }
          this.load();
        },
        error: (error: unknown) => {
          this.saving.set(false);
          this.failed.set(errorMessage(error, 'Could not update the selected leads.'));
        },
      });
  }

  protected importCsv(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) {
      return;
    }
    const body = new FormData();
    body.append('file', file);
    this.saving.set(true);
    this.http.post<{ imported: number }>('/api/crm/leads/import', body).subscribe({
      next: (result) => {
        this.saving.set(false);
        this.notice.set(`${result.imported} leads imported.`);
        this.load();
      },
      error: (error: unknown) => {
        this.saving.set(false);
        this.failed.set(errorMessage(error, 'Could not import that file.'));
      },
    });
  }

  protected saveAgent(event?: Event): void {
    event?.preventDefault();
    this.saving.set(true);
    this.http
      .post<Agent>('/api/crm/agents', {
        name: this.agentName().trim(),
        email: this.agentEmail().trim(),
        phone: this.agentPhone().trim(),
        password: this.agentPassword(),
      })
      .subscribe({
        next: () => {
          this.saving.set(false);
          this.composer.set(null);
          this.agentName.set('');
          this.agentEmail.set('');
          this.agentPhone.set('');
          this.agentPassword.set('');
          this.load();
        },
        error: (error: unknown) => {
          this.saving.set(false);
          this.failed.set(errorMessage(error, 'Could not add the agent.'));
        },
      });
  }

  protected askDelete(lead: Lead): void {
    this.pendingDelete.set(lead);
  }

  protected cancelDelete(): void {
    if (!this.deleting()) {
      this.pendingDelete.set(null);
    }
  }

  protected confirmDelete(event?: Event): void {
    event?.preventDefault();
    const lead = this.pendingDelete();
    if (!lead) {
      return;
    }
    this.deleting.set(true);
    this.http.delete(`/api/crm/leads/${lead.id}`).subscribe({
      next: () => {
        this.deleting.set(false);
        this.pendingDelete.set(null);
        this.checked.update((ids) => ids.filter((id) => id !== lead.id));
        this.leads.update((rows) => rows.filter((row) => row.id !== lead.id));
      },
      error: (error: unknown) => {
        this.deleting.set(false);
        this.pendingDelete.set(null);
        this.failed.set(errorMessage(error, 'Could not delete that lead.'));
      },
    });
  }

  protected signOut(): void {
    this.auth.signOut();
    this.account.clear();
    void this.router.navigateByUrl('/crm/signin');
  }

  protected label(status: string): string {
    return leadStatusLabel(status);
  }
}
